import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';

function timeAgo(iso) {
  if (!iso) return 'Aldrig kørt';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'Lige nu';
  if (mins < 60) return `For ${mins} min siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `For ${hours} time${hours === 1 ? '' : 'r'} siden`;
  return `For ${Math.floor(hours / 24)} dage siden`;
}

// Groups the ~24 Graph-drevet dataområder into the same categories a person already thinks in
// (matches Arbejdskø/Sidebar's own area names) so the list reads as a map instead of an alphabetical
// dump — and, just as importantly, fits two columns of short group cards without scrolling, where the
// old single flat column needed one. A routeKey missing here (a future area not yet grouped) still
// shows, just under "Andet", so nothing silently disappears.
const GROUPS = [
  { name: 'Identitet & sikkerhed', keys: ['secure-score', 'mfa-status', 'conditional-access', 'identity-risks', 'controls', 'secure-score-controls', 'permissions'] },
  { name: 'Enheder & apps', keys: ['devices', 'applications', 'intune-apps', 'intune-policies'] },
  { name: 'Grupper & samarbejde', keys: ['membership', 'groups', 'teams', 'sharepoint-sites', 'sharepoint-settings', 'tenant-resources'] },
  { name: 'Politik & revision', keys: ['policy-inventory', 'policy-scope', 'policy-results', 'policy-changes', 'audit-log'] },
  { name: 'E-mail & licenser', keys: ['exchange-insights', 'security-overview', 'licenses'] },
];

const OLD_MS = 25 * 60 * 60 * 1000;
// How the last read of one source went: Fejl (it did not work), Delvis (only part of it was read), Gammel (older than a night's
// sync should leave it), otherwise OK. The reason is shown for NordComply's administrators (the backend only sends it to them).
function statusOf(r) {
  if (!r.hasData) return { tone: 'neutral', label: '—' };
  if (r.state === 'fejl') return { tone: 'bad', label: 'Fejl' };
  if (r.state === 'delvis') return { tone: 'warn', label: 'Delvis' };
  if (r.errors.length > 0) return { tone: 'warn', label: 'Fejl' };
  if (r.syncedAt && Date.now() - new Date(r.syncedAt).getTime() > OLD_MS) return { tone: 'warn', label: 'Gammel' };
  return { tone: 'ok', label: 'OK' };
}
const KIND_TEXT = { nat: 'Nat-sync', manuel: 'Manuelt', åbning: 'Ved åbning' };
const clock = (iso) => (iso ? new Date(iso).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
export const durationText = (ms) => { const s = Math.round((ms || 0) / 1000); return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`; };

function StatusRow({ r }) {
  const s = statusOf(r);
  return (
    <div className="dk-row" title={r.errors?.length ? r.errors.join(' — ') : undefined}>
      <div className="dk-row__l">
        <b>{r.label}</b>
        <span>{timeAgo(r.syncedAt)}</span>
        {r.why && <span className="dk-why">{r.why}</span>}
      </div>
      <span className={`dk-status dk-status--${s.tone}`}>{s.label}</span>
    </div>
  );
}

// One per Graph-drevet dataområde — hvornår det sidst blev hentet, og om
// det faktisk lykkedes. Data kommer fra den samme sync-cache alle andre
// sider allerede læser (withCache i app.js), ikke en separat healthcheck —
// det her ER de reelle tidsstempler produktet bruger.
export default function SyncStatusModal({ tenantId, onClose }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/sync-status?tenantId=${tenantId}`)
      .then((r) => r.json())
      .then((result) => {
        if (cancelled) return;
        if (result.error) setError(result.error);
        else setStatus(result);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const byKey = new Map((status?.routes || []).map((r) => [r.routeKey, r]));
  const grouped = status
    ? GROUPS.map((g) => ({ name: g.name, rows: g.keys.map((k) => byKey.get(k)).filter(Boolean) })).filter((g) => g.rows.length)
    : [];
  const groupedKeys = new Set(GROUPS.flatMap((g) => g.keys));
  const rest = status ? (status.routes || []).filter((r) => !groupedKeys.has(r.routeKey)) : [];
  if (rest.length) grouped.push({ name: 'Andet', rows: rest });
  const counts = (status?.routes || []).reduce((a, r) => { const t = statusOf(r).label; a[t] = (a[t] || 0) + 1; return a; }, {});
  const columns = [[], []];
  grouped.forEach((g, i) => columns[i % 2].push(g));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--sync" onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel__head">
          <div>
            <h2>Datakilder</h2>
            <p className="modal-panel__subtitle">Hvornår hvert Microsoft Graph-område sidst blev hentet</p>
          </div>
          <button className="modal-panel__close" onClick={onClose} aria-label="Luk">×</button>
        </div>
        <div className="modal-panel__body">
          {error && <p className="modal-msg modal-msg--error">{error}</p>}
          {!status && !error && <p className="modal-section__hint">Henter status…</p>}
          {status && (
            <div className="dk-sum">
              <div><small>Sidste synkronisering</small><b>{status.lastRun ? clock(status.lastRun.at) : status.lastSyncAt ? clock(status.lastSyncAt) : 'Ingen endnu'}</b>
                {status.lastRun && <em>{KIND_TEXT[status.lastRun.kind] || status.lastRun.kind}{status.lastRun.by ? `, ${status.lastRun.by}` : ''} · varede {durationText(status.lastRun.ms)}</em>}</div>
              <div className="dk-sum__pills">
                {counts.OK > 0 && <span className="dk-status dk-status--ok">{counts.OK} OK</span>}
                {counts.Gammel > 0 && <span className="dk-status dk-status--warn">{counts.Gammel} gamle</span>}
                {counts.Delvis > 0 && <span className="dk-status dk-status--warn">{counts.Delvis} delvise</span>}
                {counts.Fejl > 0 && <span className="dk-status dk-status--bad">{counts.Fejl} med fejl</span>}
              </div>
            </div>
          )}
          {status && (
            <div className="dk-cols">
              {columns.map((col, ci) => (
                <div className="dk-col" key={ci}>
                  {col.map((g) => (
                    <div className="dk-group" key={g.name}>
                      <h4>{g.name}</h4>
                      {g.rows.map((r) => <StatusRow key={r.routeKey} r={r} />)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
